import * as React from 'react'
import { useUiEditorState } from '@/tiptap/hooks/use-ui-editor-state'
// --- Utils ---
import { isSelectionValid } from '@/tiptap/lib/tiptap-collab-utils'
// --- Icons ---
import { MoreVerticalIcon } from '@/tiptap/tiptap-icons/more-vertical-icon'
// --- Primitive UI Components ---
import type { ButtonProps } from '@/tiptap/tiptap-ui-primitive/button'
import { Button } from '@/tiptap/tiptap-ui-primitive/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/tiptap/tiptap-ui-primitive/popover'
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from '@/tiptap/tiptap-ui-primitive/toolbar'
// --- UI Utils ---
import { FloatingElement } from '@/tiptap/tiptap-ui-utils/floating-element'
// --- UI ---
import { ColorTextPopover } from '@/tiptap/tiptap-ui/color-text-popover'
import { LinkPopover } from '@/tiptap/tiptap-ui/link-popover'
import type { Mark } from '@/tiptap/tiptap-ui/mark-button'
import { canToggleMark, MarkButton } from '@/tiptap/tiptap-ui/mark-button'
import type { TextAlign } from '@/tiptap/tiptap-ui/text-align-button'
import {
  canSetTextAlign,
  TextAlignButton,
} from '@/tiptap/tiptap-ui/text-align-button'
import { TurnIntoDropdown } from '@/tiptap/tiptap-ui/turn-into-dropdown'
import { isNodeSelection, type Editor } from '@tiptap/react'
import { RemoveFormatting } from 'lucide-react'
import { Sparkles, MessageSquare, Lightbulb } from 'lucide-react'
import { useIsMobile } from '@/hooks/use-mobile'
// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor'
import { AISelector, AICompletionResolver, EditorContext } from './ai-selector'
import { LinkSelector } from './selectors'
import { FeatureGate } from '@/components/plan-restrictions/FeatureGate'

export function NotionToolbarFloating({
  completeResolver,
  enableComments = false,
  onContinueInChat,
  commentOnly = false,
}: {
  completeResolver?: AICompletionResolver
  enableComments?: boolean
  onContinueInChat?: (context: EditorContext) => void
  /** When true, only show the Comment button (for review mode) */
  commentOnly?: boolean
}) {
  const { editor } = useTiptapEditor()
  const isMobile = useIsMobile()
  const { lockDragHandle, aiGenerationActive, commentInputVisible } =
    useUiEditorState(editor)
  const [shouldShow, setShouldShow] = React.useState(false)
  const [openLink, setOpenLink] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const [aiMode, setAiMode] = React.useState<'selection' | 'ask'>('selection')
  const preventCloseOnSelectionUpdate = React.useRef(false)

  React.useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      const { state } = editor
      const { selection } = state

      // Don't auto-close if we're preventing close (e.g., just opened from slash command)
      if (preventCloseOnSelectionUpdate.current) {
        preventCloseOnSelectionUpdate.current = false
        return
      }

      setOpen(false)
      setOpenLink(false)

      const validSelection =
        isSelectionValid(editor, selection) &&
        (!isNodeSelection(selection) ||
          (isNodeSelection(selection) && selection.node.type.name === 'image'))

      if (commentInputVisible || aiGenerationActive) {
        setShouldShow(false)
        return
      }

      setShouldShow(validSelection)
    }

    handleSelectionUpdate()

    editor.on('selectionUpdate', handleSelectionUpdate)

    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate)
    }
  }, [editor, aiGenerationActive, commentInputVisible])

  // Listen for openAiSelector event from slash command
  React.useEffect(() => {
    if (!editor) return

    const handleOpenAiSelector = ({ mode }: { mode: 'selection' | 'ask' }) => {
      setAiMode(mode || 'selection')
      setOpen(true)
      // For ask mode triggered from slash, force shouldShow to true
      if (mode === 'ask') {
        setShouldShow(true)
        // Prevent the next selection update from closing the AI selector
        preventCloseOnSelectionUpdate.current = true
      }
    }

    // @ts-expect-error - custom event
    editor.on('openAiSelector', handleOpenAiSelector)

    return () => {
      // @ts-expect-error - custom event
      editor.off('openAiSelector', handleOpenAiSelector)
    }
  }, [editor])

  // Capture cursor position when opening in ask mode
  const [askModeCursorRect, setAskModeCursorRect] = React.useState<DOMRect | null>(null)

  // if (
  //   !editor.isEditable ||
  //   editor.isActive('image') ||
  //   empty ||
  //   isNodeSelection(selection) ||
  //   editor.isActive('codeBlock')
  // ) {
  //   return false
  // }
  // return true

  if (lockDragHandle || isMobile) {
    return null
  }

  // Render AI selector standalone when in ask mode (triggered from slash command)
  if (open && aiMode === 'ask') {
    // Get cursor position for positioning the AI selector (memoized on first open)
    const getCursorPosition = (editor: Editor) => {
      if (askModeCursorRect) {
        return askModeCursorRect
      }
      const { state } = editor
      const { from } = state.selection
      const coords = editor.view.coordsAtPos(from)
      const rect = new DOMRect(coords.left, coords.top, 0, coords.bottom - coords.top)
      setAskModeCursorRect(rect)
      return rect
    }

    return (
      <FloatingElement
        shouldShow={true}
        getBoundingClientRect={getCursorPosition}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setOpen(false)
            setShouldShow(false)
            setAiMode('selection')
            setAskModeCursorRect(null) // Reset position when closing
          }
        }}
      >
        <AISelector
          editor={editor}
          open={open}
          onOpenChange={(isOpen) => {
            setOpen(isOpen)
            if (!isOpen) {
              setShouldShow(false)
              setAiMode('selection')
              setAskModeCursorRect(null) // Reset position when closing
            }
          }}
          completeResolver={completeResolver}
          onContinueInChat={onContinueInChat}
          mode={aiMode}
        />
      </FloatingElement>
    )
  }

  // Comment-only mode for review workflows (no AI, no formatting)
  if (commentOnly && enableComments) {
    return (
      <FloatingElement shouldShow={shouldShow}>
        <Toolbar variant='floating'>
          <ToolbarGroup>
            <FeatureGate flag='comments'>
              <Button
                type='button'
                data-style='ghost'
                className='w-fit gap-2 !rounded-md border-none'
                onClick={() => {
                  if (!editor) {
                    return
                  }
                  editor.chain().focus().setComment('').run()
                }}
                tooltip='Add comment'
              >
                <MessageSquare className='h-4 w-4' />
                <span className='text-sm whitespace-nowrap'>Comment</span>
              </Button>
            </FeatureGate>
          </ToolbarGroup>
        </Toolbar>
      </FloatingElement>
    )
  }

  return (
    <FloatingElement shouldShow={shouldShow}>
      {open ? (
        <AISelector
          editor={editor}
          open={open}
          onOpenChange={setOpen}
          completeResolver={completeResolver!}
          onContinueInChat={onContinueInChat}
          mode={aiMode}
        />
      ) : (
        <Toolbar variant='floating'>
          <ToolbarGroup>
            <Button
              type='button'
              data-style='ghost'
              className='w-fit gap-2 !rounded-md border-none text-purple-500'
              onClick={() => setOpen(true)}
            >
              <Sparkles className='h-4 w-4' />
              <span className='text-sm whitespace-nowrap'>Ask AI</span>
            </Button>
          </ToolbarGroup>
          <ToolbarSeparator />

          {enableComments && (
            <>
              <ToolbarGroup>
                <FeatureGate flag='comments' >
                  <Button
                    type='button'
                    data-style='ghost'
                    className='w-fit gap-2 !rounded-md border-none'
                    onClick={() => {
                      if (!editor) {
                        return
                      }
                      // Call setComment without content to trigger the AddCommentPanel
                      editor.chain().focus().setComment('').run()
                    }}
                    tooltip='Add comment'
                  >
                    <MessageSquare className='h-4 w-4' />
                    <span className='text-sm whitespace-nowrap'>Comment</span>
                  </Button>
                </FeatureGate>
                {/* 按照要求， 暂时先注释这个， */}
                {/* <Button
                  type='button'
                  data-style='ghost'
                  className='w-fit gap-2 !rounded-md border-none text-purple-500'
                  onClick={() => {
                    if (!editor) {
                      return
                    }

                    // Check if text is selected
                    const { from, to } = editor.state.selection
                    const selectedText = editor.state.doc.textBetween(from, to)

                    if (!selectedText) {
                      // If no text selected, show a message
                      alert('Please select some text first to add a suggestion')
                      return
                    }

                    // Add the fake suggestion comment
                    // The command will generate appropriate suggestions based on the selected text:
                    // - Passive voice detection for text with "was/were"
                    // - Brevity suggestions for long sentences (20+ words)
                    // - Grammar suggestions (capitalization) for other text
                    editor?.commands.addFakeSuggestionComment()
                  }}
                  tooltip='Add test AI suggestion'
                >
                  <Lightbulb className='h-4 w-4' />
                  <span className='text-sm whitespace-nowrap'>Suggest</span>
                </Button> */}
              </ToolbarGroup>
              <ToolbarSeparator />
            </>
          )}

          <ToolbarGroup>
            <TurnIntoDropdown hideWhenUnavailable={true} />
          </ToolbarGroup>

          <ToolbarSeparator />

          <ToolbarGroup>
            <MarkButton type='bold' hideWhenUnavailable={true} />
            <MarkButton type='italic' hideWhenUnavailable={true} />
            <MarkButton type='strike' hideWhenUnavailable={true} />
            <MarkButton type='underline' hideWhenUnavailable={true} />
            <MarkButton type='code' hideWhenUnavailable={true} />
            <MarkButton type='kbd' hideWhenUnavailable={true} />
            <Button
              type='button'
              onClick={() => editor?.chain().focus().unsetAllMarks().run()}
              disabled={!editor?.can().unsetAllMarks()}
              data-style='ghost'
              className='h-8 w-8 !rounded-md p-0'
              tooltip='Clear formatting'
              shortcutKeys='Mod-\\'
            >
              <RemoveFormatting className='h-4 w-4' />
            </Button>
          </ToolbarGroup>

          <ToolbarSeparator />

          <ToolbarGroup>
            {/* <LinkPopover
            autoOpenOnLinkActive={false}
            hideWhenUnavailable={true}
          /> */}
            <LinkSelector
              editor={editor}
              open={openLink}
              onOpenChange={setOpenLink}
            />
          </ToolbarGroup>
          <ColorTextPopover hideWhenUnavailable={true} />
          <MoreOptions hideWhenUnavailable={true} />
        </Toolbar>
      )}
    </FloatingElement>
  )
}

function canMoreOptions(editor: Editor | null): boolean {
  if (!editor) {
    return false
  }

  const canTextAlignAny = ['left', 'center', 'right', 'justify'].some((align) =>
    canSetTextAlign(editor, align as TextAlign, true)
  )

  const canMarkAny = ['superscript', 'subscript'].some((type) =>
    canToggleMark(editor, type as Mark)
  )

  return canMarkAny || canTextAlignAny
}

function shouldShowMoreOptions(params: {
  editor: Editor | null
  hideWhenUnavailable: boolean
}): boolean {
  const { editor, hideWhenUnavailable } = params

  if (!editor) {
    return false
  }

  if (hideWhenUnavailable && !editor.isActive('code')) {
    return canMoreOptions(editor)
  }

  return Boolean(editor?.isEditable)
}

export interface MoreOptionsProps extends Omit<ButtonProps, 'type'> {
  /**
   * The Tiptap editor instance.
   */
  editor?: Editor | null
  /**
   * Whether to hide the dropdown when no options are available.
   * @default false
   */
  hideWhenUnavailable?: boolean
}

export function MoreOptions({
  editor: providedEditor,
  hideWhenUnavailable = false,
  ...props
}: MoreOptionsProps) {
  const { editor } = useTiptapEditor(providedEditor)
  const [show, setShow] = React.useState(false)

  React.useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      setShow(
        shouldShowMoreOptions({
          editor,
          hideWhenUnavailable,
        })
      )
    }

    handleSelectionUpdate()

    editor.on('selectionUpdate', handleSelectionUpdate)

    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate)
    }
  }, [editor, hideWhenUnavailable])

  if (!show || !editor || !editor.isEditable) {
    return null
  }

  return (
    <>
      <ToolbarSeparator />
      <ToolbarGroup>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type='button'
              data-style='ghost'
              role='button'
              tabIndex={-1}
              tooltip='More options'
              {...props}
            >
              <MoreVerticalIcon className='tiptap-button-icon' />
            </Button>
          </PopoverTrigger>

          <PopoverContent
            side='top'
            align='end'
            alignOffset={4}
            sideOffset={4}
            asChild
          >
            <Toolbar variant='floating' tabIndex={0}>
              <ToolbarGroup>
                <MarkButton type='superscript' />
                <MarkButton type='subscript' />
              </ToolbarGroup>

              <ToolbarSeparator />

              <ToolbarGroup>
                <TextAlignButton align='left' />
                <TextAlignButton align='center' />
                <TextAlignButton align='right' />
                <TextAlignButton align='justify' />
              </ToolbarGroup>

              <ToolbarSeparator />
            </Toolbar>
          </PopoverContent>
        </Popover>
      </ToolbarGroup>
    </>
  )
}
